<template>
  <nav class="lottery-pagination flex justify-start w-full -mt-050" role="tablist">
    <div v-drag-scroll class="lottery-pagination-wrapper">
      <TransitionGroup
        v-if="maxAllowedPlays > 1"
        class="flex gap-150 pt-050 px-200 pb-200"
        name="list"
        tag="ul"
        @before-leave="handleBeforeLeave"
      >
        <LotteryPaginationItem
          v-for="(play, playIndex) in plays"
          :key="play.key"
          ref="items"
          v-bind="{
            play,
            playIndex,
            plays,
            visiblePlayId,
          }"
          class="item"
          @click="handleClick(play.id)"
          @keydown="handleKeyDown"
        />
      </TransitionGroup>
    </div>
  </nav>
</template>

<script lang="ts" setup>
enum ActiveKeys {
  ArrowLeft = 'ArrowLeft',
  ArrowRight = 'ArrowRight',
  End = 'End',
  Home = 'Home',
}

const { getPlayIndex, maxAllowedPlays, plays, visiblePlayId } = usePlaySlip()

const itemRefs = useTemplateRef('items')

const focusedIndex = ref(plays.value.length - 1)

const focusItem = (index: number) => {
  itemRefs.value?.[index]?.focus()
}

onMounted(async () => {
  await nextTick()
  itemRefs.value?.[getPlayIndex(visiblePlayId.value)]?.scrollIntoView('instant')
})

const getNextIndex = () =>
  focusedIndex.value === plays.value.length - 1 ? 0 : focusedIndex.value + 1

const getPreviousIndex = () =>
  focusedIndex.value === 0 ? plays.value.length - 1 : focusedIndex.value - 1

const handleBeforeLeave = (element: Element) => {
  const el = element as HTMLElement
  const { height, marginTop, width } = window.getComputedStyle(el)

  const wrapper = el.closest('.lc-header') as HTMLElement
  if (!wrapper) return

  const elRect = el.getBoundingClientRect()
  const wrapperRect = wrapper.getBoundingClientRect()
  const left = elRect.left - wrapperRect.left

  element.setAttribute(
    'style',
    `
    left: ${left}px;
    top: ${el.offsetTop - parseInt(marginTop)}px;
    width: ${width};
    height: ${height};
  `,
  )
}

const handleClick = (playId: string) => {
  focusedIndex.value = getPlayIndex(playId)
  visiblePlayId.value = playId
}

const handleKeyDown = (event: KeyboardEvent) => {
  if (!isPartOfEnum(ActiveKeys, event.key)) return
  event.preventDefault()
  event.stopPropagation()

  let newIndex: number

  switch (event.key) {
    case ActiveKeys.ArrowLeft:
      newIndex = getPreviousIndex()
      break
    case ActiveKeys.ArrowRight:
      newIndex = getNextIndex()
      break
    case ActiveKeys.End:
      newIndex = plays.value.length - 1
      break
    case ActiveKeys.Home:
      newIndex = 0
      break
  }

  focusedIndex.value = newIndex
  visiblePlayId.value = plays.value[newIndex].id
}

watch(focusedIndex, focusItem)

watch(
  () => plays.value.length,
  () => {
    focusedIndex.value = getPlayIndex(visiblePlayId.value)
  },
)

watchOnce(visiblePlayId, (newValue) => {
  focusedIndex.value = getPlayIndex(newValue)
})
</script>

<style lang="scss" scoped>
.list-enter-active,
.list-leave-active,
.list-move {
  transition: all 0.5s ease;
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateY(-30px);
}

.list-leave-from {
  opacity: 1;
  transform: translateY(0);
}

.list-leave-active {
  position: absolute;
}

.lottery-pagination {
  position: relative;
  mask:
    linear-gradient(to right, transparent, black 1rem) left / 50% 100%,
    linear-gradient(to left, transparent, black 1rem) right / 50% 100%;
  mask-repeat: no-repeat;

  &-wrapper {
    display: block;

    ul {
      display: inline-flex;
      margin: auto;
    }
  }
}
</style>
